import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import Input from "../Tags/Input";
import Image from "../Tags/Image";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { useCoachBotsGet } from "../../apiHooks/useCoachBotsGet";
import { useStoreCoachBotsGet } from "../../stores/useStoreCoachBotsGet";
import SharedDataContext from "../../SharedDataContext";
import BotCard from "./BotCard";
import CallingAgent from "./CallingAgent";
import { Skeleton } from "../ui/skeleton";
import GenericModal from "../GenericModal";
import useModal from "../GenericModal/useModal";
import { Label } from "../ui/label";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import FormError from "../Tags/FormError";
import { coachAICreateBot } from "../../apis/callLogs";
import Reports from "./Reports";
import Text from "../Tags/Text";
import PageHeader from "../PageHeader";
import CallHistoryTable from "./CallHistoryTable";
import Filter from "../Filter";
import { isEmptyObject } from "../../utils/GenericFn";
import { useCaptureEvent } from "../../hooks/useCaptureEvent";
const tones = [
  {
    name: "Rude",
    key: "rude",
  },
  {
    name: "Cheerful",
    key: "cheerful",
  },
  {
    name: "Neutral",
    key: "neutral",
  },
];
const callTypes = [
  {
    name: "Cold",
    key: "cold",
  },
  {
    name: "Discovery call",
    key: "discovery",
  },
];

const difficultyLevels = [
  {
    name: "Easy",
    key: "easy",
  },
  {
    name: "Medium",
    key: "medium",
  },
  {
    name: "Hard",
    key: "hard",
  },
];

const avatarOptions = [
  "avatar_1.svg",
  "avatar_2.svg",
  "avatar_3.svg",
  "avatar_4.svg",
  "avatar_5.svg",
  "avatar_6.svg",
  "avatar_7.svg",
];

const defaultAvatar = avatarOptions[0];

const botFormInitialValues = {
  firstName: "",
  lastName: "",
  role: "",
  company: "",
  callType: "",
  tone: tones.find((tone) => tone.key === "neutral")?.key ?? "",
  avatar: defaultAvatar,
  difficultyLevel: "",
  background: "",
  additionalBehaviour: "",
};

const botFormValidationSchema = Yup.object({
  firstName: Yup.string().trim().required("Please enter first name"),
  lastName: Yup.string().trim().required("Please enter last name"),
  role: Yup.string().trim().required("Please enter job title"),
  company: Yup.string().trim().required("Please enter company name"),
  callType: Yup.string().required("Please select call type"),
  difficultyLevel: Yup.string().required("Please select difficulty"),
  tone: Yup.string().required("Please select a tone"),
  avatar: Yup.string().trim().nullable(),
  background: Yup.string().trim().max(1200, "Background details must not exceed 1200 characters").nullable(),
  additionalBehaviour: Yup.string().trim().required("Please enter behaviour guidance"),
});

export default function CoachAI() {
  const userData = useContext(SharedDataContext);
  const { activeWorkspace } = userData || {};
  const location = useLocation();
  const { coachBots, activeBotId, setActiveBot, isCallInProgress } = useStoreCoachBotsGet();
  const { isLoading, refetch } = useCoachBotsGet(activeWorkspace?.id);
  const { isOpen, openModal, closeModal } = useModal(false);
  const [searchTerm, setSearchTerm] = useState("");
  const captureEvent = useCaptureEvent();
  const formik = useFormik({
    initialValues: botFormInitialValues,
    validationSchema: botFormValidationSchema,
    onSubmit: async (values, { setSubmitting, setStatus, resetForm }) => {
      setStatus(undefined);
      captureEvent("CAI_CreateBot_SubmitClick", {
        difficulty: values.difficultyLevel,
        call_type: values.callType,
        tone: values.tone,
      });

      if (!activeWorkspace?.id) {
        setStatus({ submitError: "Select an active workspace before creating a bot." });
        setSubmitting(false);
        return;
      }

      try {
        await coachAICreateBot({
          data: {
            first_name: values.firstName.trim(),
            last_name: values.lastName.trim(),
            role: values.role.trim(),
            company: values.company.trim(),
            call_type: values.callType,
            tone: values.tone,
            avatar: values.avatar?.trim() || defaultAvatar,
            difficulty_level: values.difficultyLevel,
            background: values.background,
            additional_behaviour: values.additionalBehaviour?.trim(),
          },
          options: {
            headers: {
              "X-Workspace-Id": activeWorkspace.id,
            },
          },
        });

        await refetch?.();
        captureEvent("CAI_CreateBot_Success", {
          difficulty: values.difficultyLevel,
          call_type: values.callType,
          tone: values.tone,
        });
        resetForm();
        closeModal();
      } catch (error) {
        const apiError = error?.response?.data?.message || error?.message || "Failed to create the bot.";
        setStatus({ submitError: apiError });
        captureEvent("CAI_CreateBot_Failure", {
          error: apiError,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    if (!isOpen) {
      formik.resetForm();
      formik.setStatus(undefined);
      formik.setSubmitting(false);
    }
  }, [isOpen]);

  const [coachTab, setCoachTab] = useState(() => {
    return location.state?.openHistoryTab ? "history" : "practice";
  });
  const [viewReport, setViewReport] = useState(false);
  const [historyFilterData, setHistoryFilterData] = useState(null);
  const hasTrackedPageViewRef = useRef(false);
  const hasProcessedHistoryTabRef = useRef(false);

  useEffect(() => {
    if (location.state?.openHistoryTab && !hasProcessedHistoryTabRef.current) {
      setCoachTab("history");
      hasProcessedHistoryTabRef.current = true;
    }
  }, [location.state]);

  useEffect(() => {
    // Page view for CoachAI
    if (!hasTrackedPageViewRef.current) {
      captureEvent("CAI_PageView");
      hasTrackedPageViewRef.current = true;
    }
  }, [captureEvent]);

  const filteredBots = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!Array.isArray(coachBots)) return [];
    if (!query) return coachBots;

    return coachBots.filter((bot) => {
      const primaryName = typeof bot?.name === "string" ? bot.name : "";
      const derivedName = `${bot?.first_name ?? ""} ${bot?.last_name ?? ""}`.trim();
      const normalizedName = (primaryName || derivedName).toLowerCase();

      return normalizedName.includes(query);
    });
  }, [coachBots, searchTerm]);

  return (
    <>
      <div className="">
        <PageHeader
          heading="Coach AI"
          description="AI partners for sales reps to practice, refine pitches, and build skills."
        />
        <Tabs
          defaultValue={coachTab}
          value={coachTab}
          onValueChange={(value) => {
            setCoachTab(value);
            setViewReport(false);
            if (value !== "history") {
              setHistoryFilterData(null);
            }
            captureEvent("CAI_TabChange", { tab: value });
            if (value === "history") {
              captureEvent("CAI_History_PageView");
            }
          }}
          className="w-full"
        >
          <div className="flex -my-6 -mx-8 items-center justify-between">
            <div
              className={`w-[35%] px-8 py-4 ${
                coachTab === "practice" ? "bg-gray-iron-50 border-r border-border" : ""
              }`}
            >
              <TabsList className="mb-2">
                <TabsTrigger value="practice">Practice</TabsTrigger>
                <TabsTrigger value="history">History</TabsTrigger>
              </TabsList>
            </div>
            {coachTab === "history" &&
              historyFilterData &&
              !isEmptyObject(historyFilterData.filterMetadata) && (
                <div className="flex-1 px-8 flex justify-end items-center">
                  <div className="space-x-3 inline-flex items-center">
                    <span className="text-sm text-gray-500">Filters:</span>
                    <Filter
                      filterMetadata={historyFilterData.filterMetadata}
                      getFiltersObject={historyFilterData.getFiltersObject}
                    />
                  </div>
                </div>
              )}
          </div>
          <TabsContent value="practice" className="w-full mt-0">
            <div className="flex -my-6 -mx-8" style={{ height: "calc(100vh - 105px)" }}>
              <div className="w-[35%] bg-gray-iron-50 px-8 py-4 border-r border-border">
                <div className="w-full mt-2">
                  <div className="flex space-x-3 items-center w-full">
                    <Input
                      icon={<Image src="/images/icons/search.svg" className="no-invert w-4" />}
                      type="text"
                      placeholder="Search"
                      className="h-9"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                    />
                    {/* <Button variant="outline" className="shrink-0">
                    <Image src="/images/icons/untitledui/filter.svg" className="mr-1" /> <span>Filter</span>
                  </Button> */}
                  </div>
                </div>
                <Button
                  variant="purple_outline"
                  className="w-full h-9 mt-5"
                  onClick={() => {
                    captureEvent("CAI_CreateNewBotClick");
                    openModal();
                  }}
                >
                  <svg
                    width="19"
                    height="17"
                    viewBox="0 0 19 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2"
                  >
                    <path
                      d="M15.3346 6.33398V1.33398M12.8346 3.83398H17.8346M12.8346 15.5007V14.5007C12.8346 13.1005 12.8346 12.4005 12.5622 11.8657C12.3225 11.3953 11.94 11.0128 11.4696 10.7731C10.9348 10.5007 10.2348 10.5007 8.83464 10.5007H5.16797C3.76784 10.5007 3.06777 10.5007 2.53299 10.7731C2.06259 11.0128 1.68014 11.3953 1.44045 11.8657C1.16797 12.4005 1.16797 13.1005 1.16797 14.5007V15.5007M9.91797 4.25065C9.91797 5.86148 8.61213 7.16732 7.0013 7.16732C5.39047 7.16732 4.08464 5.86148 4.08464 4.25065C4.08464 2.63982 5.39047 1.33398 7.0013 1.33398C8.61213 1.33398 9.91797 2.63982 9.91797 4.25065Z"
                      stroke="currentColor"
                      stroke-width="1.66667"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Create new bot
                </Button>

                <div
                  className="mt-6 space-y-4 overflow-y-scroll -mx-4 px-4"
                  style={{ height: "calc(100vh - 305px)" }}
                >
                  {filteredBots &&
                    filteredBots.length > 0 &&
                    filteredBots.map((bot) => (
                      <BotCard
                        key={bot.id}
                        data={bot}
                        isActive={bot.id === activeBotId}
                        onSelect={() => {
                          setActiveBot(bot.id);
                          setViewReport(false);
                        }}
                        disabled={isCallInProgress}
                        activeWorkspace={activeWorkspace}
                        userId={userData?.id}
                      />
                    ))}
                  {coachBots?.length === 0 && isLoading && (
                    <>
                      {[1, 2, 3, 4, 5].map((el) => (
                        <Skeleton key={el} className="h-24 w-full" />
                      ))}
                    </>
                  )}
                  {!isLoading &&
                    Array.isArray(coachBots) &&
                    coachBots.length > 0 &&
                    filteredBots.length === 0 && (
                      <div className="text-sm text-text-500 text-center py-4">
                        No bots found for "{searchTerm.trim()}".
                      </div>
                    )}
                </div>
              </div>
              <div className="w-full flex-1 p-6">
                {!viewReport && (
                  <CallingAgent
                    activeWorkspace={activeWorkspace}
                    viewReport={() => {
                      captureEvent("CAI_GenerateReport_Click");
                      setViewReport(true);
                    }}
                  />
                )}
                {viewReport && (
                  <div className="-m-6">
                    <Reports />
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="history" className="w-full mt-0">
            <div className="w-full -my-6 " style={{ height: "calc(100vh - 105px)" }}>
              <CallHistoryTable onFilterReady={setHistoryFilterData} />
            </div>
            {/* Previous history tab implementation - kept for reference */}
            {/* 
              <div className="w-full mt-5">
                <div className="flex space-x-3 items-center w-full">
                  <Input
                    icon={<Image src="/images/icons/search.svg" className="no-invert w-4" />}
                    type="text"
                    placeholder="Search"
                    className="h-9"
                  />
                  <Button variant="outline" className="shrink-0">
                    <Image src="/images/icons/untitledui/filter.svg" className="mr-1" /> <span>Filter</span>
                  </Button>
                </div>
              </div>

              <div className="flex justify-center items-center flex-col space-y-3">
                <Image src="/images/coming-soon.svg" />
                <Text size="md">Coming soon...</Text>
              </div>

              <div className="mt-6 space-y-4">
                <div className="border p-3 rounded-xl bg-white flex items-start justify-between gap-3 min-h-[88px] relative">
                  <div className="inline-flex items-start space-x-3">
                    <Image
                      src="/images/avatars/profile-dummy.svg"
                      width={40}
                      height={40}
                      className="rounded-full w-10 h-10"
                    />
                    <div>
                      <span className="text-[14px] text-[#000000] block font-bold">Olivia Rhye</span>
                      <span className="text-[12px] text-[#101828] block mt-0.5">
                        Test Architect @ 23andme
                      </span>
                      <div className="flex items-center space-x-2 my-2.5">
                        <span className="inline-flex">
                          <svg
                            width="18"
                            height="17"
                            viewBox="0 0 18 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1"
                          >
                            <path
                              d="M1.5 15.6667C3.44649 13.6021 6.08918 12.3333 9 12.3333C11.9108 12.3333 14.5535 13.6021 16.5 15.6667M12.75 5.25C12.75 7.32107 11.0711 9 9 9C6.92893 9 5.25 7.32107 5.25 5.25C5.25 3.17893 6.92893 1.5 9 1.5C11.0711 1.5 12.75 3.17893 12.75 5.25Z"
                              stroke="#475467"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          Olivia Rhye
                        </span>
                        <span className="h-1 w-1 bg-gray-500 rounded-full"></span>
                        <span className="inline-flex">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1"
                          >
                            <path
                              d="M17.5 8.33268H2.5M13.3333 1.66602V4.99935M6.66667 1.66602V4.99935M6.5 18.3327H13.5C14.9001 18.3327 15.6002 18.3327 16.135 18.0602C16.6054 17.8205 16.9878 17.4381 17.2275 16.9677C17.5 16.4329 17.5 15.7328 17.5 14.3327V7.33268C17.5 5.93255 17.5 5.23249 17.2275 4.69771C16.9878 4.2273 16.6054 3.84485 16.135 3.60517C15.6002 3.33268 14.9001 3.33268 13.5 3.33268H6.5C5.09987 3.33268 4.3998 3.33268 3.86502 3.60517C3.39462 3.84485 3.01217 4.2273 2.77248 4.69771C2.5 5.23249 2.5 5.93255 2.5 7.33268V14.3327C2.5 15.7328 2.5 16.4329 2.77248 16.9677C3.01217 17.4381 3.39462 17.8205 3.86502 18.0602C4.3998 18.3327 5.09987 18.3327 6.5 18.3327Z"
                              stroke="#475467"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          12/11/23
                        </span>
                        <span className="h-1 w-1 bg-gray-500 rounded-full"></span>
                        <span className="inline-flex">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1"
                          >
                            <g clip-path="url(#clip0_12956_52255)">
                              <path
                                d="M10.0013 4.99935V9.99935L13.3346 11.666M18.3346 9.99935C18.3346 14.6017 14.6037 18.3327 10.0013 18.3327C5.39893 18.3327 1.66797 14.6017 1.66797 9.99935C1.66797 5.39698 5.39893 1.66602 10.0013 1.66602C14.6037 1.66602 18.3346 5.39698 18.3346 9.99935Z"
                                stroke="#475467"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_12956_52255">
                                <rect width="20" height="20" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          2 mins
                        </span>
                      </div>
                      <div className="mt-3 flex space-x-2">
                        <Badge variant="success" className="bg-green-200 text-green-600 font-bold">
                          92 Success
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="pr-2 pt-2">
                    <svg
                      width="4"
                      height="18"
                      viewBox="0 0 4 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 10C2.55228 10 3 9.55228 3 9C3 8.44772 2.55228 8 2 8C1.44772 8 1 8.44772 1 9C1 9.55228 1.44772 10 2 10Z"
                        stroke="#535862"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M2 3C2.55228 3 3 2.55228 3 2C3 1.44772 2.55228 1 2 1C1.44772 1 1 1.44772 1 2C1 2.55228 1.44772 3 2 3Z"
                        stroke="#535862"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M2 17C2.55228 17 3 16.5523 3 16C3 15.4477 2.55228 15 2 15C1.44772 15 1 15.4477 1 16C1 16.5523 1.44772 17 2 17Z"
                        stroke="#535862"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>

                <div className="border p-3 rounded-xl bg-white flex items-start justify-between gap-3 min-h-[88px] relative">
                  <div className="inline-flex items-start space-x-3">
                    <Image
                      src="/images/avatars/profile-dummy.svg"
                      width={40}
                      height={40}
                      className="rounded-full w-10 h-10"
                    />
                    <div>
                      <span className="text-[14px] text-[#000000] block font-bold">Olivia Rhye</span>
                      <span className="text-[12px] text-[#101828] block mt-0.5">
                        Test Architect @ 23andme
                      </span>
                      <div className="flex items-center space-x-2 my-2.5">
                        <span className="inline-flex">
                          <svg
                            width="18"
                            height="17"
                            viewBox="0 0 18 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1"
                          >
                            <path
                              d="M1.5 15.6667C3.44649 13.6021 6.08918 12.3333 9 12.3333C11.9108 12.3333 14.5535 13.6021 16.5 15.6667M12.75 5.25C12.75 7.32107 11.0711 9 9 9C6.92893 9 5.25 7.32107 5.25 5.25C5.25 3.17893 6.92893 1.5 9 1.5C11.0711 1.5 12.75 3.17893 12.75 5.25Z"
                              stroke="#475467"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          Olivia Rhye
                        </span>
                        <span className="h-1 w-1 bg-gray-500 rounded-full"></span>
                        <span className="inline-flex">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1"
                          >
                            <path
                              d="M17.5 8.33268H2.5M13.3333 1.66602V4.99935M6.66667 1.66602V4.99935M6.5 18.3327H13.5C14.9001 18.3327 15.6002 18.3327 16.135 18.0602C16.6054 17.8205 16.9878 17.4381 17.2275 16.9677C17.5 16.4329 17.5 15.7328 17.5 14.3327V7.33268C17.5 5.93255 17.5 5.23249 17.2275 4.69771C16.9878 4.2273 16.6054 3.84485 16.135 3.60517C15.6002 3.33268 14.9001 3.33268 13.5 3.33268H6.5C5.09987 3.33268 4.3998 3.33268 3.86502 3.60517C3.39462 3.84485 3.01217 4.2273 2.77248 4.69771C2.5 5.23249 2.5 5.93255 2.5 7.33268V14.3327C2.5 15.7328 2.5 16.4329 2.77248 16.9677C3.01217 17.4381 3.39462 17.8205 3.86502 18.0602C4.3998 18.3327 5.09987 18.3327 6.5 18.3327Z"
                              stroke="#475467"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          12/11/23
                        </span>
                        <span className="h-1 w-1 bg-gray-500 rounded-full"></span>
                        <span className="inline-flex">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1"
                          >
                            <g clip-path="url(#clip0_12956_52255)">
                              <path
                                d="M10.0013 4.99935V9.99935L13.3346 11.666M18.3346 9.99935C18.3346 14.6017 14.6037 18.3327 10.0013 18.3327C5.39893 18.3327 1.66797 14.6017 1.66797 9.99935C1.66797 5.39698 5.39893 1.66602 10.0013 1.66602C14.6037 1.66602 18.3346 5.39698 18.3346 9.99935Z"
                                stroke="#475467"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_12956_52255">
                                <rect width="20" height="20" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          2 mins
                        </span>
                      </div>
                      <div className="mt-3 flex space-x-2">
                        <Badge variant="error" className="bg-red-200 text-red-600 font-bold">
                          12 Failed
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="pr-2 pt-2">
                    <svg
                      width="4"
                      height="18"
                      viewBox="0 0 4 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 10C2.55228 10 3 9.55228 3 9C3 8.44772 2.55228 8 2 8C1.44772 8 1 8.44772 1 9C1 9.55228 1.44772 10 2 10Z"
                        stroke="#535862"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M2 3C2.55228 3 3 2.55228 3 2C3 1.44772 2.55228 1 2 1C1.44772 1 1 1.44772 1 2C1 2.55228 1.44772 3 2 3Z"
                        stroke="#535862"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M2 17C2.55228 17 3 16.5523 3 16C3 15.4477 2.55228 15 2 15C1.44772 15 1 15.4477 1 16C1 16.5523 1.44772 17 2 17Z"
                        stroke="#535862"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>

                <div className="border p-3 rounded-xl bg-white flex items-start justify-between gap-3 min-h-[88px] relative">
                  <div className="inline-flex items-start space-x-3">
                    <Image
                      src="/images/avatars/profile-dummy.svg"
                      width={40}
                      height={40}
                      className="rounded-full w-10 h-10"
                    />
                    <div>
                      <span className="text-[14px] text-[#000000] block font-bold">Olivia Rhye</span>
                      <span className="text-[12px] text-[#101828] block mt-0.5">
                        Test Architect @ 23andme
                      </span>
                      <div className="flex items-center space-x-2 my-2.5">
                        <span className="inline-flex">
                          <svg
                            width="18"
                            height="17"
                            viewBox="0 0 18 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1"
                          >
                            <path
                              d="M1.5 15.6667C3.44649 13.6021 6.08918 12.3333 9 12.3333C11.9108 12.3333 14.5535 13.6021 16.5 15.6667M12.75 5.25C12.75 7.32107 11.0711 9 9 9C6.92893 9 5.25 7.32107 5.25 5.25C5.25 3.17893 6.92893 1.5 9 1.5C11.0711 1.5 12.75 3.17893 12.75 5.25Z"
                              stroke="#475467"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          Olivia Rhye
                        </span>
                        <span className="h-1 w-1 bg-gray-500 rounded-full"></span>
                        <span className="inline-flex">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1"
                          >
                            <path
                              d="M17.5 8.33268H2.5M13.3333 1.66602V4.99935M6.66667 1.66602V4.99935M6.5 18.3327H13.5C14.9001 18.3327 15.6002 18.3327 16.135 18.0602C16.6054 17.8205 16.9878 17.4381 17.2275 16.9677C17.5 16.4329 17.5 15.7328 17.5 14.3327V7.33268C17.5 5.93255 17.5 5.23249 17.2275 4.69771C16.9878 4.2273 16.6054 3.84485 16.135 3.60517C15.6002 3.33268 14.9001 3.33268 13.5 3.33268H6.5C5.09987 3.33268 4.3998 3.33268 3.86502 3.60517C3.39462 3.84485 3.01217 4.2273 2.77248 4.69771C2.5 5.23249 2.5 5.93255 2.5 7.33268V14.3327C2.5 15.7328 2.5 16.4329 2.77248 16.9677C3.01217 17.4381 3.39462 17.8205 3.86502 18.0602C4.3998 18.3327 5.09987 18.3327 6.5 18.3327Z"
                              stroke="#475467"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          12/11/23
                        </span>
                        <span className="h-1 w-1 bg-gray-500 rounded-full"></span>
                        <span className="inline-flex">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1"
                          >
                            <g clip-path="url(#clip0_12956_52255)">
                              <path
                                d="M10.0013 4.99935V9.99935L13.3346 11.666M18.3346 9.99935C18.3346 14.6017 14.6037 18.3327 10.0013 18.3327C5.39893 18.3327 1.66797 14.6017 1.66797 9.99935C1.66797 5.39698 5.39893 1.66602 10.0013 1.66602C14.6037 1.66602 18.3346 5.39698 18.3346 9.99935Z"
                                stroke="#475467"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_12956_52255">
                                <rect width="20" height="20" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          2 mins
                        </span>
                      </div>
                      <div className="mt-3 flex space-x-2">
                        <Badge variant="success" className="bg-green-200 text-green-600 font-bold">
                          92 Success
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="pr-2 pt-2">
                    <svg
                      width="4"
                      height="18"
                      viewBox="0 0 4 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 10C2.55228 10 3 9.55228 3 9C3 8.44772 2.55228 8 2 8C1.44772 8 1 8.44772 1 9C1 9.55228 1.44772 10 2 10Z"
                        stroke="#535862"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M2 3C2.55228 3 3 2.55228 3 2C3 1.44772 2.55228 1 2 1C1.44772 1 1 1.44772 1 2C1 2.55228 1.44772 3 2 3Z"
                        stroke="#535862"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M2 17C2.55228 17 3 16.5523 3 16C3 15.4477 2.55228 15 2 15C1.44772 15 1 15.4477 1 16C1 16.5523 1.44772 17 2 17Z"
                        stroke="#535862"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>

                <div className="border p-3 rounded-xl bg-white flex items-start justify-between gap-3 min-h-[88px] relative">
                  <div className="inline-flex items-start space-x-3">
                    <Image
                      src="/images/avatars/profile-dummy.svg"
                      width={40}
                      height={40}
                      className="rounded-full w-10 h-10"
                    />
                    <div>
                      <span className="text-[14px] text-[#000000] block font-bold">Olivia Rhye</span>
                      <span className="text-[12px] text-[#101828] block mt-0.5">
                        Test Architect @ 23andme
                      </span>
                      <div className="flex items-center space-x-2 my-2.5">
                        <span className="inline-flex">
                          <svg
                            width="18"
                            height="17"
                            viewBox="0 0 18 17"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1"
                          >
                            <path
                              d="M1.5 15.6667C3.44649 13.6021 6.08918 12.3333 9 12.3333C11.9108 12.3333 14.5535 13.6021 16.5 15.6667M12.75 5.25C12.75 7.32107 11.0711 9 9 9C6.92893 9 5.25 7.32107 5.25 5.25C5.25 3.17893 6.92893 1.5 9 1.5C11.0711 1.5 12.75 3.17893 12.75 5.25Z"
                              stroke="#475467"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          Olivia Rhye
                        </span>
                        <span className="h-1 w-1 bg-gray-500 rounded-full"></span>
                        <span className="inline-flex">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1"
                          >
                            <path
                              d="M17.5 8.33268H2.5M13.3333 1.66602V4.99935M6.66667 1.66602V4.99935M6.5 18.3327H13.5C14.9001 18.3327 15.6002 18.3327 16.135 18.0602C16.6054 17.8205 16.9878 17.4381 17.2275 16.9677C17.5 16.4329 17.5 15.7328 17.5 14.3327V7.33268C17.5 5.93255 17.5 5.23249 17.2275 4.69771C16.9878 4.2273 16.6054 3.84485 16.135 3.60517C15.6002 3.33268 14.9001 3.33268 13.5 3.33268H6.5C5.09987 3.33268 4.3998 3.33268 3.86502 3.60517C3.39462 3.84485 3.01217 4.2273 2.77248 4.69771C2.5 5.23249 2.5 5.93255 2.5 7.33268V14.3327C2.5 15.7328 2.5 16.4329 2.77248 16.9677C3.01217 17.4381 3.39462 17.8205 3.86502 18.0602C4.3998 18.3327 5.09987 18.3327 6.5 18.3327Z"
                              stroke="#475467"
                              stroke-width="2"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>
                          12/11/23
                        </span>
                        <span className="h-1 w-1 bg-gray-500 rounded-full"></span>
                        <span className="inline-flex">
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            className="mr-1"
                          >
                            <g clip-path="url(#clip0_12956_52255)">
                              <path
                                d="M10.0013 4.99935V9.99935L13.3346 11.666M18.3346 9.99935C18.3346 14.6017 14.6037 18.3327 10.0013 18.3327C5.39893 18.3327 1.66797 14.6017 1.66797 9.99935C1.66797 5.39698 5.39893 1.66602 10.0013 1.66602C14.6037 1.66602 18.3346 5.39698 18.3346 9.99935Z"
                                stroke="#475467"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_12956_52255">
                                <rect width="20" height="20" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                          2 mins
                        </span>
                      </div>
                      <div className="mt-3 flex space-x-2">
                        <Badge variant="error" className="bg-red-200 text-red-600 font-bold">
                          12 Failed
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="pr-2 pt-2">
                    <svg
                      width="4"
                      height="18"
                      viewBox="0 0 4 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 10C2.55228 10 3 9.55228 3 9C3 8.44772 2.55228 8 2 8C1.44772 8 1 8.44772 1 9C1 9.55228 1.44772 10 2 10Z"
                        stroke="#535862"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M2 3C2.55228 3 3 2.55228 3 2C3 1.44772 2.55228 1 2 1C1.44772 1 1 1.44772 1 2C1 2.55228 1.44772 3 2 3Z"
                        stroke="#535862"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M2 17C2.55228 17 3 16.5523 3 16C3 15.4477 2.55228 15 2 15C1.44772 15 1 15.4477 1 16C1 16.5523 1.44772 17 2 17Z"
                        stroke="#535862"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            */}
          </TabsContent>
        </Tabs>
      </div>
      <GenericModal isOpen={isOpen} closeModal={closeModal} width="w-[1000px]">
        <div>
          <h2 className="text-base font-semibold leading-7 ">Create a new AI bot</h2>
          <p className="text-paragraph">Set up a bot by defining its identity and key details.</p>
        </div>
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} className="mt-6 space-y-10">
            {formik.status?.submitError && (
              <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                {formik.status.submitError}
              </div>
            )}

            <div className="grid gap-8 md:grid-cols-[320px,1fr] border-t border-border pt-7">
              <div>
                <div className="text-sm font-semibold text-text-900">Basic details</div>
                <p className="mt-2 text-sm text-text-600">
                  Set the bot's name, gender, and other call related traits to properly configure the bot.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="coach-bot-first-name">First name *</Label>
                  <Input
                    id="coach-bot-first-name"
                    name="firstName"
                    placeholder="Enter first name"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <FormError field="firstName" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coach-bot-last-name">Last name *</Label>
                  <Input
                    id="coach-bot-last-name"
                    name="lastName"
                    placeholder="Enter last name"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <FormError field="lastName" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coach-bot-difficulty">Set difficulty *</Label>
                  <select
                    id="coach-bot-difficulty"
                    name="difficultyLevel"
                    value={formik.values.difficultyLevel}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="h-11 w-full rounded-[--radius] border border-input bg-background px-[14px] text-sm text-text-900"
                  >
                    <option value="" disabled>
                      Select difficulty
                    </option>
                    {difficultyLevels.map((level) => (
                      <option key={level.key} value={level.key}>
                        {level.name}
                      </option>
                    ))}
                  </select>
                  <FormError field="difficultyLevel" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coach-bot-call-type">Call type *</Label>
                  <select
                    id="coach-bot-call-type"
                    name="callType"
                    value={formik.values.callType}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="h-11 w-full rounded-[--radius] border border-input bg-background px-[14px] text-sm text-text-900"
                  >
                    <option value="" disabled>
                      Select call type
                    </option>
                    {callTypes.map((type) => (
                      <option key={type.key} value={type.key}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  <FormError field="callType" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coach-bot-tone">Tone</Label>
                  <select
                    id="coach-bot-tone"
                    name="tone"
                    value={formik.values.tone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="h-11 w-full rounded-[--radius] border border-input bg-background px-[14px] text-sm text-text-900"
                  >
                    {tones.map((tone) => (
                      <option key={tone.key} value={tone.key}>
                        {tone.name}
                      </option>
                    ))}
                  </select>
                  <FormError field="tone" />
                </div>
                <div className="space-y-2">
                  <Label>Avatar</Label>
                  <div className="grid grid-cols-4 gap-4 sm:grid-cols-7">
                    {avatarOptions.map((avatar) => {
                      const isSelected = formik.values.avatar === avatar;
                      return (
                        <button
                          key={avatar}
                          type="button"
                          onClick={() => formik.setFieldValue("avatar", avatar)}
                          aria-pressed={isSelected}
                          className={`flex h-11 w-11 items-center justify-center rounded-full border transition-all ${
                            isSelected
                              ? "border-brand-600 ring-2 ring-brand-100"
                              : "border-border hover:border-brand-300"
                          }`}
                        >
                          <Image
                            src={`/images/bots/${avatar}`}
                            alt={avatar}
                            className="h-10 w-10 rounded-full object-cover no-invert"
                          />
                        </button>
                      );
                    })}
                  </div>
                  {/* <p className="text-xs text-text-500">
                    Selected avatar: {formik.values.avatar || defaultAvatar}
                  </p> */}
                </div>
              </div>
            </div>

            <div className="grid gap-8 md:grid-cols-[320px,1fr]  border-t border-border pt-7">
              <div>
                <div className="text-sm font-semibold text-text-900">Company details *</div>
                <p className="mt-2 text-sm text-text-600">
                  Provide the company's status and context to help the bot align with the organization's
                  needs.
                </p>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="coach-bot-company">Company name *</Label>
                  <Input
                    id="coach-bot-company"
                    name="company"
                    placeholder="Enter company name"
                    value={formik.values.company}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <FormError field="company" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="coach-bot-role">Job title *</Label>
                  <Input
                    id="coach-bot-role"
                    name="role"
                    placeholder="Enter job title"
                    value={formik.values.role}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <FormError field="role" />
                </div>
              </div>
            </div>
            <div className="grid gap-8 md:grid-cols-[320px,1fr]  border-t border-border pt-7">
              <div>
                <div className="text-sm font-semibold text-text-900">Background</div>
                <p className="mt-2 text-sm text-text-600">
                  Add key background details that will help users understand the prospect.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="coach-bot-background">Background details</Label>
                <textarea
                  id="coach-bot-background"
                  name="background"
                  placeholder="Enter background details"
                  value={formik.values.background}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="min-h-[60px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-text-900 focus:outline-none focus:ring-1 focus:ring-ring"
                />
                <FormError field="background" />
              </div>
            </div>

            <div className="grid gap-8 md:grid-cols-[320px,1fr]  border-t border-border pt-7">
              <div>
                <div className="text-sm font-semibold text-text-900">Behaviour, rules and objections *</div>
                <p className="mt-2 text-sm text-text-600">
                  Outline the company's main priorities, behaviour, guardrails and objections to guide the
                  bot's responses.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="coach-bot-behaviour">Behaviour guidance</Label>
                <textarea
                  id="coach-bot-behaviour"
                  name="additionalBehaviour"
                  placeholder="Enter priorities or goals"
                  value={formik.values.additionalBehaviour}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className="min-h-[60px] w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-text-900 focus:outline-none focus:ring-1 focus:ring-ring"
                />
                <FormError field="additionalBehaviour" />
              </div>
            </div>

            <div className="flex justify-end space-x-3 border-t border-border pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  formik.resetForm();
                  formik.setStatus(undefined);
                  closeModal();
                }}
                disabled={formik.isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={formik.isSubmitting}>
                {formik.isSubmitting ? "Creating..." : "Create bot"}
              </Button>
            </div>
          </form>
        </FormikProvider>
      </GenericModal>
    </>
  );
}
